import { createApi } from '@reduxjs/toolkit/query/react';
import { GuidT, PaginationIF } from '@/types/common';
import {
    AddClothingRequestIF,
    ClothingIF,
    DeleteClothingRequestIF,
    RawClothingIF,
    SearchClothingRequestIF,
    UpdateClothingRequestIF,
} from '@/api/clothingApi/types';
import { outfitsApi } from '@/api/outfitsApi/outfitsApi';
import { fetchRestApiQuery } from '@/helpers/api';
import {
    normalizeClothing,
    normalizeClothingList,
} from '@/helpers/normalizeApi';

export const clothingApi = createApi({
    reducerPath: 'clothingApi',
    baseQuery: fetchRestApiQuery('/Clothings'),
    tagTypes: ['SearchClothing', 'Clothing'],
    endpoints: (builder) => ({
        getClothingById: builder.query<ClothingIF, GuidT>({
            query: (clothingGuid: GuidT) => `/${clothingGuid}`,
            transformResponse(response: RawClothingIF) {
                return normalizeClothing(response);
            },
            providesTags: (_result, _error, guid) => [
                { type: 'Clothing', id: guid },
            ],
        }),
        addClothing: builder.mutation<ClothingIF, AddClothingRequestIF>({
            query: (body) => ({
                url: '',
                method: 'POST',
                body,
            }),
            transformResponse(response: RawClothingIF) {
                return normalizeClothing(response);
            },
            invalidatesTags: [{ type: 'SearchClothing' }],
        }),
        deleteClothing: builder.mutation<void, DeleteClothingRequestIF>({
            query: ({ clothingGuid, force }) => ({
                url: `/${clothingGuid}`,
                method: 'DELETE',
                params: force ? { Force: true } : undefined,
            }),
            async onQueryStarted(_argument, { dispatch, queryFulfilled }) {
                await queryFulfilled;

                dispatch(
                    outfitsApi.util.invalidateTags([
                        'SearchOutfits',
                        'GetOutfitById',
                    ]),
                );
            },
            invalidatesTags: [{ type: 'SearchClothing' }],
        }),
        updateClothing: builder.mutation<void, UpdateClothingRequestIF>({
            query: (body) => ({
                url: `/${body.guid}`,
                method: 'PUT',
                body: body,
            }),
            async onQueryStarted(_argument, { dispatch, queryFulfilled }) {
                await queryFulfilled;

                dispatch(
                    outfitsApi.util.invalidateTags([
                        'SearchOutfits',
                        'GetOutfitById',
                    ]),
                );
            },
            invalidatesTags: (_result, _error, { guid }) => [
                { type: 'Clothing', id: guid },
                { type: 'SearchClothing' },
            ],
        }),
        searchClothing: builder.query<
            PaginationIF<ClothingIF>,
            SearchClothingRequestIF
        >({
            query: (parameters) => {
                const searchParameters = new URLSearchParams();

                if (parameters.page) {
                    searchParameters.append('Page', String(parameters.page));
                }

                if (parameters.pageSize) {
                    searchParameters.append(
                        'PageSize',
                        String(parameters.pageSize),
                    );
                }

                parameters.categoriesGuid?.forEach((id) =>
                    searchParameters.append('CategoriesGuid', id),
                );

                if (parameters.text) {
                    searchParameters.append('Text', parameters.text);
                }

                if (parameters.brandGuid) {
                    searchParameters.append('BrandGuid', parameters.brandGuid);
                }

                parameters.colorGuids?.forEach((id) =>
                    searchParameters.append('ColorsGuid', id),
                );

                parameters.guids?.forEach((id) =>
                    searchParameters.append('Guids', id),
                );

                if (parameters.suitableSeasons?.length) {
                    searchParameters.append(
                        'SuitableSeason',
                        parameters.suitableSeasons.join('|'),
                    );
                }

                if (parameters.sortMember) {
                    searchParameters.append(
                        'SortMember',
                        parameters.sortMember,
                    );
                }

                if (parameters.ascending !== undefined) {
                    searchParameters.append(
                        'Ascending',
                        String(parameters.ascending),
                    );
                }

                return {
                    url: `?${searchParameters.toString()}`,
                };
            },
            transformResponse(response: {
                total: number;
                data: RawClothingIF[];
            }) {
                return {
                    total: response.total,
                    data: normalizeClothingList(response.data),
                };
            },
            providesTags: [{ type: 'SearchClothing' }],
        }),
    }),
});
